import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Bookmark,
  Database,
  Server,
  Loader2,
  Trash2,
  Play,
  Pencil,
  Copy,
  Search,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const SavedQueriesPage = () => {
  const { getAuthHeader } = useAuth();
  const { selectedConnection } = useConnection();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [selectedConn, setSelectedConn] = useState("");

  useEffect(() => {
    if (selectedConnection && selectedConnection.id) {
      setSelectedConn(selectedConnection.id);
    }
  }, [selectedConnection]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuery, setEditingQuery] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", query: "", description: "" });

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    fetchQueries();
  }, [selectedConn]);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API_URL}/connections`, {
        headers: getAuthHeader()
      });
      setConnections(response.data);
    } catch (error) {
      toast.error("Failed to fetch connections");
    }
  };

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const url = selectedConn 
        ? `${API_URL}/query/saved?connection_id=${selectedConn}`
        : `${API_URL}/query/saved`;
      const response = await axios.get(url, {
        headers: getAuthHeader()
      });
      setQueries(response.data);
    } catch (error) {
      toast.error("Failed to fetch saved queries");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/query/saved/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("Query deleted");
      fetchQueries();
    } catch (error) {
      toast.error("Failed to delete query");
    }
  };

  const handleEdit = (query) => {
    setEditingQuery(query);
    setEditForm({
      name: query.name,
      query: query.query,
      description: query.description || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editForm.name.trim()) {
      toast.error("Please enter a name");
      return;
    }
    try {
      await axios.put(`${API_URL}/query/saved/${editingQuery.id}`, {
        name: editForm.name,
        connection_id: editingQuery.connection_id,
        query: editForm.query,
        description: editForm.description
      }, { headers: getAuthHeader() });
      toast.success("Query updated");
      setEditDialogOpen(false);
      fetchQueries();
    } catch (error) {
      toast.error("Failed to update query");
    }
  };

  const handleRunQuery = (queryText, connId, name) => {
    // Store the query text to be loaded in QueryEditorPage
    sessionStorage.setItem('pendingQuery', JSON.stringify({ query: queryText, connId, name }));
    navigate('/dashboard/query');
  };

  const handleCopyQuery = (query) => {
    navigator.clipboard.writeText(query);
    toast.success("Query copied to clipboard");
  };

  const getConnectionName = (connId) => {
    const conn = connections.find(c => c.id === connId);
    return conn?.name || 'Unknown';
  };

  const getConnectionType = (connId) => {
    const conn = connections.find(c => c.id === connId);
    return conn?.db_type || 'unknown';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const filteredQueries = queries.filter(q => 
    q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (q.description && q.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-6">
        <div>
          <h1 className="text-xl font-bold">Saved Queries</h1>
          <p className="text-sm text-muted-foreground">Manage your saved queries</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search queries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="search-saved-queries-input"
            />
          </div>
          <Select value={selectedConn || "all"} onValueChange={(value) => setSelectedConn(value === "all" ? "" : value)}>
            <SelectTrigger className="w-48" data-testid="saved-connection-filter">
              <SelectValue placeholder="All connections" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All connections</SelectItem>
              {connections.map(conn => (
                <SelectItem key={conn.id} value={conn.id}>
                  <div className="flex items-center gap-2">
                    {conn.db_type === 'mongodb' ? (
                      <Database className="w-4 h-4 text-green-500" />
                    ) : (
                      <Server className="w-4 h-4 text-blue-500" />
                    )}
                    {conn.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredQueries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <Bookmark className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No saved queries</h3>
              <p className="text-muted-foreground">
                {queries.length === 0 
                  ? "Save queries from the Query Editor to access them here"
                  : "No queries match your search"}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredQueries.map(query => (
                <Card key={query.id} className="bg-card border-border hover:border-border/80 transition-colors" data-testid={`saved-query-card-${query.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">{query.name}</CardTitle>
                        <CardDescription className="text-xs mt-1 flex items-center gap-2">
                          {getConnectionType(query.connection_id) === 'mongodb' ? (
                            <Database className="w-3 h-3 text-green-500" />
                          ) : (
                            <Server className="w-3 h-3 text-blue-500" />
                          )}
                          {getConnectionName(query.connection_id)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <code className="text-xs font-mono bg-muted/30 px-3 py-2 rounded-md block truncate">
                      {query.query}
                    </code>
                    {query.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {query.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Updated: {formatDate(query.updated_at)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1"
                        onClick={() => handleRunQuery(query.query, query.connection_id, query.name)}
                        data-testid={`run-saved-query-btn-${query.id}`}
                      >
                        <Play className="w-3 h-3" />
                        Run
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyQuery(query.query)}
                        data-testid={`copy-saved-query-btn-${query.id}`}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(query)}
                        data-testid={`edit-saved-query-btn-${query.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-saved-query-btn-${query.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Saved Query</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{query.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(query.id)} className="bg-destructive hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Saved Query</DialogTitle>
            <DialogDescription>
              Update your saved query
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="editName">Name *</Label>
              <Input
                id="editName"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="edit-query-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editQuery">Query *</Label>
              <Textarea
                id="editQuery"
                value={editForm.query}
                onChange={(e) => setEditForm(prev => ({ ...prev, query: e.target.value }))}
                className="font-mono text-sm min-h-32"
                data-testid="edit-query-textarea"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editDesc">Description</Label>
              <Input
                id="editDesc"
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="edit-query-desc-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate} data-testid="update-query-btn">Update Query</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedQueriesPage;
